import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseComponent } from '@app/core/bases';
import { Brand, Category, ProductFilters, Range, SubCategory } from '@app/core/interfaces';
import { BrandService, CategoryService, ProductService, RangeService, SearchService, SubCategoryFilters, SubCategoryService, UtilityService } from '@app/core/services';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectChangeEvent, MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { SelectChangeEvent, SelectFilterEvent, SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { TagModule } from 'primeng/tag';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { Button } from "primeng/button";
import { DividerModule } from 'primeng/divider';
import { Column } from '../product-table/product-table';

export interface SearchFilter {
  brands: Brand | null;
  references: string | null;
  ids: string | null;
  stocks: number | null;
  quantities: number | null;
  states: any[] | [];
  priceRange: [number, number] | [];
  gammes: Range | null;
  categories: Category | null;
  subCategories: SubCategory | null;
  recherche_globale?: string | null;
}

@Component({
  selector: 'app-product-filter',
  imports: [CommonModule, PanelModule, SelectModule, MultiSelectModule, FormsModule, SliderModule, InputNumberModule, TagModule, InputTextModule, Button, DividerModule],
  templateUrl: './product-filter.html'
})
export class ProductFilter extends BaseComponent implements OnInit {
  public utilityService = inject(UtilityService);

  private _columns!: Column[];
  get columns(): Column[] {
    return this._columns;
  }
  @Input() set columns(value: Column[]) {
    this._columns = value;
    this.showGamme = value.find(col => col.field === 'gamme') ? true : false;
    this.showCategory = value.find(col => col.field === 'categorie') ? true : false;
    this.showSubCategory = value.find(col => col.field === 'sous_categorie') ? true : false;
  }
  @Output() onFiltersChange = new EventEmitter<SearchFilter>();

  brands: Brand[] = [];
  brandSubject = new Subject<string>();

  references: string[] = [];
  referenceSubject = new Subject<string>();

  gammes: Range[] = [];
  gammeSubject = new Subject<string>();

  categories: Category[] = [];
  categorySubject = new Subject<string>();

  subCategories: SubCategory[] = [];
  subCategorySubject = new Subject<string>();

  minPrice: number = 0;
  maxPrice: number = 1000;
  maxLimit: number = 1000;
  priceRange: [number, number] = [this.minPrice, this.maxPrice];
  
  states: any[] = [
    { name: 'Neuf', code: 'NEUF', severity: 'success' },
    { name: 'Reconditionné', code: 'RECONDITIONNE', severity: 'info' },
    { name: 'Occasion', code: 'OCCASION', severity: 'warn' },
    { name: 'Défectueux', code: 'HS', severity: 'danger' }
  ];

  stocks: any[] = [1, 2, 3];
  quantities: any[] = [1, 2, 3];

  showGamme = false;
  showCategory = false;
  showSubCategory = false;

  filters: SearchFilter = {
    brands: null,
    references: null,
    ids: null,
    stocks: null,
    quantities: null,
    states: [],
    priceRange: this.priceRange,
    gammes: null,
    categories: null,
    subCategories: null
  };

  constructor(
    private searchService: SearchService,
    private brandService: BrandService,
    private rangeService: RangeService,
    private categoryService: CategoryService,
    private subCategoryService: SubCategoryService,
    private productService: ProductService
  ) {
    super();

    this.brandSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.brandRequest(value));
    this.referenceSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.referenceRequest(value));
    this.gammeSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.categoryRequest(value));
    this.categorySubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.categoryRequest(value));
    this.subCategorySubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.subCategoryRequest(value));
  }

  ngOnInit(): void {
    const savedFilters = this.storageService.searchFilter;
    if (savedFilters) {
      this.filters = { ...savedFilters } as SearchFilter;
      this.onFiltersChange.emit(this.filters);
    }
  }

  onBrandFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.brandSubject.next(value);
  }

  brandRequest(value: string) {
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.brandService
      .getCollection({ nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { marques: 'id,nom' } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.brands = data.results,
        complete: () => this.apiCall$?.unsubscribe()
      });
  }

  onReferenceFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.referenceSubject.next(value);
  }

  referenceRequest(value: string) {
    const filter: ProductFilters = { reference: value, size: 10, order_by: 'reference', order: 'ASC', fields: { produits: 'reference' } };
    if (this.filters.brands) filter.marque_id = this.filters.brands.id;

    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.productService
      .getCollection(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.references = data.results.map(item => item.reference ?? '') || [],
        complete: () => this.apiCall$?.unsubscribe()
      });
  }

  onGammeFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.gammeSubject.next(value);
  }

  gammeRequest(value: string) {
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.rangeService
      .getCollection({ nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { gammes: 'id,nom' } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.gammes = data.results,
        complete: () => this.apiCall$?.unsubscribe()
      });
  }

  onCategoryFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.categorySubject.next(value);
  }

  categoryRequest(value: string) {
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.categoryService
      .getCollection({ nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { categories: 'id,nom' } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.categories = data.results,
        complete: () => this.apiCall$?.unsubscribe()
      });
  }

  onSubCategoryFilter(event: SelectFilterEvent) {
    const value = event.filter;
    this.subCategorySubject.next(value);
  }

  subCategoryRequest(value: string) {
    const filter: SubCategoryFilters = { nom: value, size: 10, order_by: 'nom', order: 'ASC', fields: { 'sous_categories': 'id,nom' } };
    if (this.filters.categories) filter.categorie_id = this.filters.categories.id;

    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.subCategoryService
      .getCollection(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.subCategories = data.results,
        complete: () => this.apiCall$?.unsubscribe()
      });
  }

  onSelectReferenceChange(event: SelectChangeEvent) {
    this.referenceSubject.next(event.value);
    this.onSaveFilters();
    this.onFiltersChange.emit(this.filters);
  }

  onSelectChange(event: SelectChangeEvent) {
    this.onSaveFilters();
    this.onFiltersChange.emit(this.filters);
  }

  onMultiSelectChange(event: MultiSelectChangeEvent) {
    this.onSaveFilters();
    this.onFiltersChange.emit(this.filters);
  }

  onInput() {
    this.priceRange = [this.minPrice < 0 ? 0 : this.minPrice, this.maxPrice > this.maxLimit ? this.maxLimit : this.maxPrice];
    this.filters.priceRange = this.priceRange;
    this.onSaveFilters();
    this.onFiltersChange.emit(this.filters);
  }

  onChange() {
    this.minPrice = this.priceRange[0];
    this.maxPrice = this.priceRange[1];
    this.filters.priceRange = this.priceRange;
    this.onSaveFilters();
    this.onFiltersChange.emit(this.filters);
  }

  onClear() {
    this.filters = {
      brands: null,
      references: null,
      ids: null,
      stocks: null, 
      quantities: null, 
      states: [],
      priceRange: [0, this.maxLimit],
      gammes: null,
      categories: null,
      subCategories: null
    };
    this.priceRange = [0, this.maxLimit];
    this.minPrice = 0;
    this.maxPrice = this.maxLimit;
    this.searchService.clearSearchTerm();
    this.onSaveFilters();
    this.onFiltersChange.emit(this.filters);
  }

  onSaveFilters() {
    this.storageService.searchFilter = this.filters;
  }
}
